import React, { useState, useEffect } from 'react';
import {
  Card, Table, Tabs, Space, Button, Tag, Modal, Descriptions,
  Statistic, Row, Col, Select, Input, message, Tooltip, Typography, Collapse, Divider
} from 'antd';
import {
  EyeOutlined, ReloadOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  InfoCircleOutlined, BarChartOutlined, ClockCircleOutlined,
  FileTextOutlined, DatabaseOutlined, MessageOutlined, SendOutlined
} from '@ant-design/icons';
import { useI18n } from '../hooks/useI18n';
import {
  ibuildProxyService,
  AuditEvent,
  RequestMapping,
  ResponseMessage,
  StreamMessage,
  QueryParams
} from '../services/ibuildProxyService';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { Text } = Typography;

interface Stats {
  totalAuditEvents: number;
  totalRequestMappings: number;
  totalResponseMessages: number;
  totalStreamMessages: number;
  successRate: number;
  failedCount: number;
}

const IBuildProxyDataViewer: React.FC = () => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('audit-events');
  
  // 数据状态
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [requestMappings, setRequestMappings] = useState<RequestMapping[]>([]);
  const [responseMessages, setResponseMessages] = useState<ResponseMessage[]>([]);
  const [streamMessages, setStreamMessages] = useState<StreamMessage[]>([]);
  
  // 分页状态
  const [auditPagination, setAuditPagination] = useState({ total: 0, limit: 20, offset: 0 });
  const [requestPagination, setRequestPagination] = useState({ total: 0, limit: 20, offset: 0 });
  const [responsePagination, setResponsePagination] = useState({ total: 0, limit: 20, offset: 0 });
  const [streamPagination, setStreamPagination] = useState({ total: 0, limit: 20, offset: 0 });
  
  // 详情状态
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  
  // 筛选条件
  const [filters, setFilters] = useState<QueryParams>({
    requestId: '',
    status: '',
    stage: '',
    messageType: '',
    limit: 20,
    offset: 0,
    orderBy: 'created_at',
    orderDirection: 'DESC'
  });
  
  // 统计信息
  const [stats, setStats] = useState<Stats>({
    totalAuditEvents: 0,
    totalRequestMappings: 0,
    totalResponseMessages: 0,
    totalStreamMessages: 0,
    successRate: 0,
    failedCount: 0
  });

  useEffect(() => {
    loadAllData();
  }, [activeTab]);

  const loadAllData = async () => {
    await Promise.all([
      loadAuditEvents(),
      loadRequestMappings(),
      loadResponseMessages(),
      loadStreamMessages()
    ]);
    updateStats();
  };

  const loadAuditEvents = async () => {
    try {
      setLoading(true);
      const params: QueryParams = {
        ...filters,
        limit: filters.limit || 20,
        offset: auditPagination.offset,
        orderBy: 'id',
        orderDirection: 'DESC'
      };
      const response = await ibuildProxyService.getAuditEvents(params);
      setAuditEvents(response.data);
      setAuditPagination(response.pagination);
    } catch (error: any) {
      message.error(`加载审计事件失败: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadRequestMappings = async () => {
    try {
      const params: QueryParams = {
        ...filters,
        limit: filters.limit || 20,
        offset: requestPagination.offset
      };
      const response = await ibuildProxyService.getRequestMappings(params);
      setRequestMappings(response.data);
      setRequestPagination(response.pagination);
    } catch (error: any) {
      message.error(`加载请求映射失败: ${error.message}`);
    }
  };

  const loadResponseMessages = async () => {
    try {
      const params: QueryParams = {
        ...filters,
        limit: filters.limit || 20,
        offset: responsePagination.offset
      };
      const response = await ibuildProxyService.getResponseMessages(params);
      setResponseMessages(response.data);
      setResponsePagination(response.pagination);
    } catch (error: any) {
      message.error(`加载响应消息失败: ${error.message}`);
    }
  };

  const loadStreamMessages = async () => {
    try {
      const params: QueryParams = {
        ...filters,
        limit: filters.limit || 20,
        offset: streamPagination.offset
      };
      const response = await ibuildProxyService.getStreamMessages(params);
      setStreamMessages(response.data);
      setStreamPagination(response.pagination);
    } catch (error: any) {
      message.error(`加载流式消息失败: ${error.message}`);
    }
  };

  const updateStats = () => {
    const totalAuditEvents = auditPagination.total;
    const totalRequestMappings = requestPagination.total;
    const totalResponseMessages = responsePagination.total;
    const totalStreamMessages = streamPagination.total;
    
    // 计算成功率和失败数
    const failedAuditEvents = auditEvents.filter(e => e.status === 'failed').length;
    const totalProcessed = auditEvents.length;
    const successRate = totalProcessed > 0 
      ? Math.round(((totalProcessed - failedAuditEvents) / totalProcessed) * 100)
      : 0;
    
    setStats({
      totalAuditEvents,
      totalRequestMappings,
      totalResponseMessages,
      totalStreamMessages,
      successRate,
      failedCount: failedAuditEvents
    });
  };

  const handleViewDetail = (record: any) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, offset: 0 }));
  };

  const handleSearch = () => {
    if (activeTab === 'audit-events') {
      loadAuditEvents();
    } else if (activeTab === 'request-mappings') {
      loadRequestMappings();
    } else if (activeTab === 'response-messages') {
      loadResponseMessages();
    } else if (activeTab === 'stream-messages') {
      loadStreamMessages();
    }
  };

  const handleTableChange = (pagination: any, tableType: string) => {
    const offset = (pagination.current - 1) * pagination.pageSize;
    if (tableType === 'audit') {
      setAuditPagination(prev => ({ ...prev, offset, limit: pagination.pageSize }));
      setTimeout(() => loadAuditEvents(), 0);
    } else if (tableType === 'request') {
      setRequestPagination(prev => ({ ...prev, offset, limit: pagination.pageSize }));
      setTimeout(() => loadRequestMappings(), 0);
    } else if (tableType === 'response') {
      setResponsePagination(prev => ({ ...prev, offset, limit: pagination.pageSize }));
      setTimeout(() => loadResponseMessages(), 0);
    } else if (tableType === 'stream') {
      setStreamPagination(prev => ({ ...prev, offset, limit: pagination.pageSize }));
      setTimeout(() => loadStreamMessages(), 0);
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      success: { color: 'success', text: '成功' },
      failed: { color: 'error', text: '失败' },
      pending: { color: 'default', text: '待处理' },
      processing: { color: 'processing', text: '处理中' },
      completed: { color: 'success', text: '完成' },
    };

    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const auditEventsColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '请求ID',
      dataIndex: 'request_id',
      key: 'request_id',
      width: 200,
      ellipsis: true,
    },
    {
      title: '阶段',
      dataIndex: 'stage',
      key: 'stage',
      width: 120,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: getStatusTag
    },
    {
      title: '消息',
      dataIndex: 'message',
      key: 'message',
      width: 200,
      ellipsis: true,
      render: (text: string) => text || '-'
    },
    {
      title: '错误信息',
      dataIndex: 'error_message',
      key: 'error_message',
      width: 200,
      ellipsis: true,
      render: (text: string) => text ? <Text type="danger">{text}</Text> : '-'
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_: any, record: AuditEvent) => (
        <Tooltip title="查看详情">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          />
        </Tooltip>
      )
    }
  ];

  const requestMappingsColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '请求ID',
      dataIndex: 'request_id',
      key: 'request_id',
      width: 200,
      ellipsis: true,
    },
    {
      title: '租户代码',
      dataIndex: 'tenant_code',
      key: 'tenant_code',
      width: 120,
    },
    {
      title: 'ChatBox ID',
      dataIndex: 'chat_box_id',
      key: 'chat_box_id',
      width: 150,
      ellipsis: true,
      render: (text: string) => text || '-'
    },
    {
      title: 'IBuild请求ID',
      dataIndex: 'ibuild_request_id',
      key: 'ibuild_request_id',
      width: 200,
      ellipsis: true,
      render: (text: string) => text || '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: getStatusTag
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_: any, record: RequestMapping) => (
        <Tooltip title="查看详情">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          />
        </Tooltip>
      )
    }
  ];

  const responseMessagesColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '请求ID',
      dataIndex: 'request_id',
      key: 'request_id',
      width: 200,
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: getStatusTag
    },
    {
      title: '重试次数',
      dataIndex: 'retry_count',
      key: 'retry_count',
      width: 100,
    },
    {
      title: '响应数据',
      dataIndex: 'response_data',
      key: 'response_data',
      width: 200,
      ellipsis: true,
      render: (text: string) => text ? <Text code>{text.substring(0, 50)}...</Text> : '-'
    },
    {
      title: '错误信息',
      dataIndex: 'error_message',
      key: 'error_message',
      width: 200,
      ellipsis: true,
      render: (text: string) => text ? <Text type="danger">{text}</Text> : '-'
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_: any, record: ResponseMessage) => (
        <Tooltip title="查看详情">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          />
        </Tooltip>
      )
    }
  ];

  const streamMessagesColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '请求ID',
      dataIndex: 'request_id',
      key: 'request_id',
      width: 200,
      ellipsis: true,
    },
    {
      title: '消息类型',
      dataIndex: 'message_type',
      key: 'message_type',
      width: 120,
      render: (type: string) => <Tag>{type}</Tag>
    },
    {
      title: '是否发送',
      dataIndex: 'is_sent',
      key: 'is_sent',
      width: 100,
      render: (sent: boolean) => sent ? (
        <Tag color="success"><SendOutlined /> 已发送</Tag>
      ) : (
        <Tag color="default">未发送</Tag>
      )
    },
    {
      title: '序号',
      dataIndex: 'sequence_number',
      key: 'sequence_number',
      width: 80,
      render: (num: number) => num || '-'
    },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      width: 300,
      ellipsis: true,
      render: (text: string) => text ? <Text code>{text.substring(0, 100)}...</Text> : '-'
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_: any, record: StreamMessage) => (
        <Tooltip title="查看详情">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          />
        </Tooltip>
      )
    }
  ];

  return (
    <div style={{ padding: '20px' }}>
      <Row gutter={16} style={{ marginBottom: '20px' }}>
        <Col span={24}>
          <Card 
            title="IBuild Proxy 数据查询" 
            extra={
              <Button 
                icon={<ReloadOutlined />} 
                onClick={loadAllData}
                loading={loading}
              >
                刷新
              </Button>
            }
          >
            <Row gutter={16}>
              <Col span={4}>
                <Statistic
                  title="审计事件"
                  value={stats.totalAuditEvents}
                  prefix={<FileTextOutlined />}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="请求映射"
                  value={stats.totalRequestMappings}
                  prefix={<DatabaseOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="响应消息"
                  value={stats.totalResponseMessages}
                  prefix={<MessageOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="流式消息"
                  value={stats.totalStreamMessages}
                  prefix={<SendOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="失败数"
                  value={stats.failedCount}
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="成功率"
                  value={stats.successRate}
                  suffix="%"
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ 
                    color: stats.successRate >= 95 ? '#3f8600' : 
                           stats.successRate >= 80 ? '#faad14' : '#cf1322' 
                  }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: '20px' }}>
        <Col span={24}>
          <Card title="筛选条件" size="small">
            <Space wrap>
              <Input
                placeholder="请求ID"
                style={{ width: 200 }}
                value={filters.requestId}
                onChange={(e) => handleFilterChange('requestId', e.target.value)}
                allowClear
              />
              <Select
                placeholder="状态"
                style={{ width: 120 }}
                allowClear
                value={filters.status || undefined}
                onChange={(value) => handleFilterChange('status', value)}
              >
                <Select.Option value="success">成功</Select.Option>
                <Select.Option value="failed">失败</Select.Option>
                <Select.Option value="pending">待处理</Select.Option>
                <Select.Option value="processing">处理中</Select.Option>
              </Select>
              <Select
                placeholder="阶段"
                style={{ width: 120 }}
                allowClear
                value={filters.stage || undefined}
                onChange={(value) => handleFilterChange('stage', value)}
              >
                <Select.Option value="request">请求</Select.Option>
                <Select.Option value="response">响应</Select.Option>
                <Select.Option value="callback">回调</Select.Option>
              </Select>
              <Select
                placeholder="消息类型"
                style={{ width: 120 }}
                allowClear
                value={filters.messageType || undefined}
                onChange={(value) => handleFilterChange('messageType', value)}
              >
                <Select.Option value="START">START</Select.Option>
                <Select.Option value="MERGE">MERGE</Select.Option>
                <Select.Option value="END">END</Select.Option>
                <Select.Option value="SINGLE">SINGLE</Select.Option>
              </Select>
              <Button type="primary" onClick={handleSearch}>
                筛选
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        type="card"
      >
        <TabPane
          tab={
            <span>
              <FileTextOutlined />
              审计事件 ({auditEvents.length})
            </span>
          }
          key="audit-events"
        >
          <Table
            columns={auditEventsColumns}
            dataSource={auditEvents}
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ x: 1200 }}
            pagination={{
              current: Math.floor(auditPagination.offset / auditPagination.limit) + 1,
              pageSize: auditPagination.limit,
              total: auditPagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
            }}
            onChange={(pagination) => handleTableChange(pagination, 'audit')}
          />
        </TabPane>

        <TabPane
          tab={
            <span>
              <DatabaseOutlined />
              请求映射 ({requestMappings.length})
            </span>
          }
          key="request-mappings"
        >
          <Table
            columns={requestMappingsColumns}
            dataSource={requestMappings}
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ x: 1200 }}
            pagination={{
              current: Math.floor(requestPagination.offset / requestPagination.limit) + 1,
              pageSize: requestPagination.limit,
              total: requestPagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
            }}
            onChange={(pagination) => handleTableChange(pagination, 'request')}
          />
        </TabPane>

        <TabPane
          tab={
            <span>
              <MessageOutlined />
              响应消息 ({responseMessages.length})
            </span>
          }
          key="response-messages"
        >
          <Table
            columns={responseMessagesColumns}
            dataSource={responseMessages}
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ x: 1200 }}
            pagination={{
              current: Math.floor(responsePagination.offset / responsePagination.limit) + 1,
              pageSize: responsePagination.limit,
              total: responsePagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
            }}
            onChange={(pagination) => handleTableChange(pagination, 'response')}
          />
        </TabPane>

        <TabPane
          tab={
            <span>
              <SendOutlined />
              流式消息 ({streamMessages.length})
            </span>
          }
          key="stream-messages"
        >
          <Table
            columns={streamMessagesColumns}
            dataSource={streamMessages}
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ x: 1200 }}
            pagination={{
              current: Math.floor(streamPagination.offset / streamPagination.limit) + 1,
              pageSize: streamPagination.limit,
              total: streamPagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
            }}
            onChange={(pagination) => handleTableChange(pagination, 'stream')}
          />
        </TabPane>
      </Tabs>

      {/* 详情模态框 */}
      <Modal
        title="详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={1000}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
      >
        {selectedRecord && (() => {
          // 定义长文本字段
          const longTextFields = ['payload', 'metadata', 'error', 'error_message', 'response_data', 'content'];
          
          // 分离长文本字段和短字段
          const shortFields: Record<string, any> = {};
          const longFields: Record<string, any> = {};
          
          Object.entries(selectedRecord).forEach(([key, value]) => {
            if (longTextFields.includes(key.toLowerCase())) {
              longFields[key] = value;
            } else {
              shortFields[key] = value;
            }
          });

          // 尝试格式化 JSON 字符串
          const formatJsonValue = (value: any): string => {
            if (typeof value === 'string') {
              try {
                const parsed = JSON.parse(value);
                return JSON.stringify(parsed, null, 2);
              } catch {
                return value;
              }
            }
            return String(value || '');
          };

          return (
            <div>
              {/* 短字段使用 Descriptions */}
              <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
                {Object.entries(shortFields).map(([key, value]) => (
                  <Descriptions.Item key={key} label={key}>
                    {value === null || value === undefined ? (
                      <Text type="secondary">-</Text>
                    ) : typeof value === 'boolean' ? (
                      <Tag color={value ? 'success' : 'default'}>{value ? '是' : '否'}</Tag>
                    ) : typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/) ? (
                      dayjs(value).format('YYYY-MM-DD HH:mm:ss')
                    ) : (
                      String(value)
                    )}
                  </Descriptions.Item>
                ))}
              </Descriptions>

              {/* 长文本字段使用 Collapse */}
              {Object.keys(longFields).length > 0 && (
                <>
                  <Divider>长文本字段</Divider>
                  <Collapse
                    items={Object.entries(longFields).map(([key, value]) => ({
                      key,
                      label: (
                        <span style={{ fontWeight: 'bold' }}>
                          {key}
                          {value && typeof value === 'string' && (
                            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                              ({value.length} 字符)
                            </Text>
                          )}
                        </span>
                      ),
                      children: (
                        <Input.TextArea
                          value={formatJsonValue(value)}
                          readOnly
                          autoSize={{ minRows: 3, maxRows: 8 }}
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            backgroundColor: '#f5f5f5'
                          }}
                        />
                      )
                    }))}
                    defaultActiveKey={Object.keys(longFields)}
                  />
                </>
              )}
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default IBuildProxyDataViewer;

